// ── Create Series ("/cohorts/:cohort_id/sessions/series") — Wave 6 ──────────
// The session-series create flow:
//   1. PREVIEW: POST /cohorts/:id/sessions/series/preview (idempotency-keyed) —
//      a NON-WRITE that returns the previewed session instances + a confirmation
//      token. We show the previewed instances; NOTHING is created yet.
//   2. CREATE: POST /cohorts/:id/sessions/series (idempotency-keyed) — carries the
//      preview confirmation token; creates the session records.
// The Zoom scheduling lane is GATED-DARK: the create makes session RECORDS but
// the meeting provider is not created (dry-run / provider-closed). We render that
// HONESTLY — "sessions created; no Zoom meetings/links (provider scheduling is
// closed)" — and NEVER a fake meeting/link.
//
// ROLE-GATING: SA/Admin may create series within scope; COACH only when
// operator-approved (the default-OFF flag → gated/disabled with an honest reason).
// Read-only roles never reach the write affordance. A participant is denied the
// whole Sessions module (route + this page's fail-closed guard). The cohort id is
// a STRING — passed VERBATIM, never Number()'d.

import { Alert, Button, Input, Module, ModuleBody, ModuleHead, PageFrame, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HonestState } from "../../components/honest-state";
import { useSessionWorkflowMutations } from "../../hooks/useSessionWorkflowMutations";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingOperationResult } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  canAccessSessionsModule,
  canUseCreateSeries,
  describeSessionWriteError,
  isCoachCreateSeriesApproved,
  isValidCohortId
} from "./sessions-helpers";
import { SeriesPreviewTable } from "./create-series/series-preview-table";

export function CreateSeriesPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useSessionWorkflowMutations();

  const cohortId = liveRecordIdFromPath(routePath, "cohorts");
  const validId = isValidCohortId(cohortId);
  const moduleDenied = !canAccessSessionsModule(role);
  // Capability (write roles) AND the approval flag for coach.
  const capabilityAllowed = evaluateActionCapability(role, "sessions:create-series").allowed;
  const allowed = capabilityAllowed && canUseCreateSeries(role);

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [preview, setPreview] = useState<CoachingOperationResult | null>(null);
  const [created, setCreated] = useState<CoachingOperationResult | null>(null);

  // ── Fail closed: denied module OR not-allowed role -> honest state, NO write ─
  if (moduleDenied) {
    return (
      <SeriesShell>
        <HonestState
          posture="permission-denied"
          title="You don't have access to create sessions"
          message="Session series creation is for coaching staff."
        />
      </SeriesShell>
    );
  }
  if (!allowed) {
    // Coach-not-approved is the gated case; other roles are read-only/denied.
    const coachPending = role === "coach" && !isCoachCreateSeriesApproved();
    return (
      <SeriesShell>
        <HonestState
          posture="gated"
          title={coachPending ? "Create Series is pending approval" : "You can't create a session series"}
          message={
            coachPending
              ? "Creating a session series for your cohorts needs operator approval. This is disabled until it's enabled for coaches."
              : "Your role can view sessions but can't create a series. Ask a super admin if you need this."
          }
        />
      </SeriesShell>
    );
  }
  if (!validId) {
    return (
      <SeriesShell>
        <HonestState posture="empty" title="No cohort selected" message="Open Create Series from a cohort." />
      </SeriesShell>
    );
  }

  const canPreview = startDate.trim().length > 0 && startTime.trim().length > 0 && timezone.trim().length > 0;
  const previewReady = preview?.ok === true;
  const previewError = describeSessionWriteError(mutations.previewSeries.error);
  const createError = describeSessionWriteError(mutations.createSeries.error);
  const previewBlocked = preview && preview.ok === false ? preview : null;

  function runPreview() {
    setCreated(null);
    mutations.createSeries.reset();
    void mutations.previewSeries
      .mutateAsync({
        cohortId: cohortId as string,
        body: { start_date: startDate.trim(), start_time: startTime.trim(), source_timezone: timezone.trim() }
      })
      .then((result) => setPreview(result))
      .catch(() => {
        // surfaced via mutations.previewSeries.error
      });
  }

  function runCreate() {
    void mutations.createSeries
      .mutateAsync({
        cohortId: cohortId as string,
        body: {
          start_date: startDate.trim(),
          start_time: startTime.trim(),
          source_timezone: timezone.trim(),
          confirmed_timezone: true,
          // Echo the DST confirmation token — the live create REQUIRES the exact
          // token minted by the preview (previewConfirmation.token); the mock's
          // flat `reference` stays as fallback for mock-mode parity.
          ...((preview?.previewConfirmation?.token ?? preview?.reference)
            ? { previewConfirmationToken: preview?.previewConfirmation?.token ?? preview?.reference }
            : {})
        }
      })
      .then((result) => {
        setCreated(result);
        if (result.ok) {
          mutations.previewSeries.reset();
          setPreview(null);
          toast.success("Session series created (Zoom scheduling pending)");
        }
      })
      .catch(() => {
        // surfaced via mutations.createSeries.error
      });
  }

  return (
    <SeriesShell>
      <Module span={12}>
        <ModuleHead
          title="Create session series"
          subtitle="Preview the generated sessions, then create them. Zoom meetings are scheduled separately."
        />
        <ModuleBody className="space-y-4">
          {previewError && <Alert variant={previewError.tone === "warning" ? "warning" : "error"}>{previewError.message}</Alert>}
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label="Start date"
              type="date"
              aria-label="Start date"
              value={startDate}
              onChange={(event) => {
                setStartDate(event.target.value);
                setPreview(null);
              }}
            />
            <Input
              label="Start time"
              type="time"
              aria-label="Start time"
              value={startTime}
              onChange={(event) => {
                setStartTime(event.target.value);
                setPreview(null);
              }}
            />
            <Input
              label="Timezone"
              aria-label="Timezone"
              value={timezone}
              onChange={(event) => {
                setTimezone(event.target.value);
                setPreview(null);
              }}
            />
          </div>
          <div>
            <Button
              variant="secondary"
              onClick={runPreview}
              disabled={!canPreview}
              isLoading={mutations.previewSeries.isPending}
              aria-label="Preview series"
            >
              Preview series
            </Button>
          </div>

          {previewBlocked ? (
            <Alert variant="warning">
              This series can't be previewed yet ({previewBlocked.status ?? "blocked"}). No sessions were created.
            </Alert>
          ) : null}

          {previewReady ? (
            <div className="space-y-3" data-testid="series-preview">
              <Alert variant="info">
                These sessions will be created. Zoom meetings and join links are not configured in this environment, so
                no meeting is created on confirm — only the session records.
              </Alert>
              <SeriesPreviewTable result={preview} />
              {createError && <Alert variant={createError.tone === "warning" ? "warning" : "error"}>{createError.message}</Alert>}
              <div className="flex gap-3">
                <Button variant="primary" onClick={runCreate} isLoading={mutations.createSeries.isPending} aria-label="Create series">
                  Create series
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setPreview(null);
                    mutations.previewSeries.reset();
                  }}
                  disabled={mutations.createSeries.isPending}
                >
                  Discard preview
                </Button>
              </div>
            </div>
          ) : null}

          {created?.ok ? (
            <Alert variant="success">
              Session series created. Sessions are scheduled; Zoom meetings stay pending until provider scheduling is
              enabled.
            </Alert>
          ) : created && created.ok === false ? (
            <Alert variant="warning">
              The series wasn't created ({created.status ?? "blocked"}). No change was made.
            </Alert>
          ) : null}
        </ModuleBody>
      </Module>
    </SeriesShell>
  );
}

function SeriesShell({ children }: { children: React.ReactNode }) {
  return (
    <PageFrame title="Create session series" subtitle="Preview the generated sessions, then create them. Zoom meetings are scheduled separately." className="col-span-12">
      <section aria-label="Create session series" className="flex flex-col gap-4" data-testid="create-series-page">
        <Link to="/sessions" className="text-link no-underline hover:underline">
          ← Back to sessions
        </Link>
        <div className="rounded-xl border border-line bg-white p-0">{children}</div>
      </section>
    </PageFrame>
  );
}
